// Função auxiliar para inferir tipo de mídia baseado no mimetype
function inferMediaTypeFromMime(mimetype = '') {
  if (mimetype.startsWith('audio/')) return 'audio';
  if (mimetype.startsWith('image/')) return 'image';
  if (mimetype.startsWith('video/')) return 'video';
  return 'document';
}

// HKDF simplificado usando Web Crypto
async function hkdf(inputKey, salt, info, length) {
  try {
    // Usar a API nativa do browser se disponível
    const baseKey = await crypto.subtle.importKey(
      'raw',
      inputKey,
      { name: 'HKDF', hash: 'SHA-256' },
      false,
      ['deriveBits']
    );
    
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: salt,
        info: info
      },
      baseKey,
      length * 8
    );
    
    return new Uint8Array(derivedBits);
  } catch (e) {
    // Fallback: usar HKDF manual
    return fallbackHKDF(inputKey, salt, info, length);
  }
}

// Fallback HKDF manual
async function fallbackHKDF(inputKey, salt, info, length) {
  const hashLen = 32; // SHA-256
  
  // HKDF-Extract: PRK = HMAC-Hash(salt, IKM)
  const extractKey = await crypto.subtle.importKey(
    'raw',
    inputKey,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  const prk = new Uint8Array(
    await crypto.subtle.sign('HMAC', extractKey, salt)
  );
  
  // HKDF-Expand: OKM = T(1) | T(2) | ... | T(N)
  const blocksNeeded = Math.ceil(length / hashLen);
  const okm = new Uint8Array(length);
  
  for (let i = 0; i < blocksNeeded; i++) {
    const T = new Uint8Array(info.length + 1);
    T.set(info);
    T[info.length] = i + 1;
    
    const expandKey = await crypto.subtle.importKey(
      'raw',
      prk,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const hash = new Uint8Array(
      await crypto.subtle.sign('HMAC', expandKey, T)
    );
    
    const offset = i * hashLen;
    const copyLen = Math.min(hashLen, length - offset);
    okm.set(hash.slice(0, copyLen), offset);
  }
  
  return okm;
}

// Função para decodificar mídia do WhatsApp
export async function decryptWhatsAppMedia(encryptedData, mediaKey, mimetype) {
  try {
    // 1) Separar corpo e MAC (últimos 10 bytes)
    const macFromServer = encryptedData.slice(encryptedData.length - 10);
    const file = encryptedData.slice(0, encryptedData.length - 10);

    // 2) Derivar chaves via HKDF (112 bytes)
    const mediaType = inferMediaTypeFromMime(mimetype);
    const mediaKeyBytes = Uint8Array.from(atob(mediaKey), c => c.charCodeAt(0));
    
    const HKDF_INFO = {
      audio: 'WhatsApp Audio Keys',
      image: 'WhatsApp Image Keys', 
      video: 'WhatsApp Video Keys',
      document: 'WhatsApp Document Keys'
    };
    
    const salt = new Uint8Array(32);
    const info = new TextEncoder().encode(HKDF_INFO[mediaType]);
    
    const expanded = await hkdf(mediaKeyBytes, salt, info, 112);
    
    const iv = expanded.slice(0, 16);
    const cipherKey = expanded.slice(16, 48);
    const macKey = expanded.slice(48, 80);

    // 3) Validar HMAC (primeiros 10 bytes)
    const dataToMac = new Uint8Array(iv.length + file.length);
    dataToMac.set(iv, 0);
    dataToMac.set(file, iv.length);
    
    const macKeyForHmac = await crypto.subtle.importKey(
      'raw',
      macKey,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const macCalc = await crypto.subtle.sign('HMAC', macKeyForHmac, dataToMac);
    const macCalcTruncated = new Uint8Array(macCalc.slice(0, 10));
    
    // Comparar MACs
    const macsEqual = macCalcTruncated.every((byte, index) => byte === macFromServer[index]);
    
    if (!macsEqual) {
      // HMAC inválido, mas continuando com decodificação
    }

    // 4) Descriptografar AES-256-CBC + PKCS7
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      cipherKey,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );
    
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv
      },
      cryptoKey,
      file
    );

    return new Uint8Array(decryptedData);
    
  } catch (error) {
    throw error;
  }
}

// Função para decodificar imagem usando mediaKey
export async function decodeImageWithMediaKey(imageUrl, mediaKey, mimetype) {
  try {
    // Buscar dados da imagem
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        'Referer': 'https://web.whatsapp.com/'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const encryptedData = await response.arrayBuffer();
    
    // Decodificar usando mediaKey
    const decryptedData = await decryptWhatsAppMedia(
      new Uint8Array(encryptedData), 
      mediaKey, 
      mimetype
    );
    
    // Criar blob e URL para a imagem decodificada
    const blob = new Blob([decryptedData], { type: mimetype });
    const decodedImageUrl = URL.createObjectURL(blob);
    
    return decodedImageUrl;
    
  } catch (error) {
    throw error;
  }
}

